import {soundAssets} from "../../config/config";
import {getAudioContext} from "../audioContext";
import {ISchedulePlayNote} from "../synthesizer/ISchedulePlayNote";
import {midiForOne, midiForTwo, midiForThree, midiForFour, midiForSet, midiForGo} from "./timeSignatureCountInMap";
import {timeSignatureCountInMap} from "./timeSignatureCountInMap";
import Note from "../../models/soundslice/Note";
import NotePath from "../../models/soundslice/NotePath";
import NotesPlaybackScheduler from "../playbackOrchestrator/NotesPlaybackScheduler";
import accurateTimingWorker from "../accurateTimingWorker";
import {subscribeToSettingsEvents} from "../eventBus/settings/settingsEventBus";
import {SettingsEventTypes} from "../eventBus/settings/SettingsEventTypes";
import InstrumentSampleAsyncPlayer from "../synthesizer/InstrumentSampleAsyncPlayer";
import {logger} from "../log/logger";

//we create notes for each word said in the countin. A midi number is assigned to the note so we know which sample to play.
interface IMidiNumberSamplePlayerHash {
  [key: number]: InstrumentSampleAsyncPlayer;
}

/**
 * Plays David's voice for count in into the song.
 */
class CountIn implements ISchedulePlayNote{
  //control volume for count in
  gainNode: GainNode;
  //sample players for "one" "two" "set" "go" , etc
  samplePlayers: IMidiNumberSamplePlayerHash;
  //how we accurately schedule playback.
  notesPlaybackScheduler = new NotesPlaybackScheduler();
  //we calculate how long it'll take for the count in to complete (2 bars, with bar duration and speed factored in)
  clearTimeoutForCountInCompleted: ((forceCallToCallback: boolean) => void ) | undefined;

  // we use the time that midi SET starts to calculate how long to wait after playing midi GO
  midiForSetStartedAtMs?: number;
  
  // call back that is called to resolve countIn play
  protected onCompletedCallback?: () => void;

  constructor(){
    this.createAndLoadWebAudioNodes();

    subscribeToSettingsEvents((message: SettingsEventTypes)=>{
      switch (message.type) {
        case "audioContextSampleRateChanged":
          this.createAndLoadWebAudioNodes();
          break;
        case "volumesChanged":
          this.setVolume(message.volumes.countIn);
          break;
      }
    });
  }

  setVolume(volume: number){
    this.gainNode.gain.value = volume;
  }

  createAndLoadWebAudioNodes(){
    this.gainNode = getAudioContext().createGain();
    this.samplePlayers = buildCountInSamplePlayers(this.gainNode);
    void this.loadSounds();
    this.gainNode.connect(getAudioContext().destination);
  }

  /**
   * Downloads sounds used in count in. e.g. "one", "two", etc
   */
  async loadSounds(){
    return Promise.all([
      this.samplePlayers[midiForOne].load(),
      this.samplePlayers[midiForTwo].load(),
      this.samplePlayers[midiForThree].load(),
      this.samplePlayers[midiForFour].load(),
      this.samplePlayers[midiForGo].load(),
      this.samplePlayers[midiForSet].load(),
    ]);
  }

  protected setOnCompletedCallback(callback?: () => void){
    this.onCompletedCallback = callback;
  }

  protected runOnCompletedCallback(): void {
    if (this.onCompletedCallback) {
      this.onCompletedCallback();
    }
    // clear on completed callback after run
    this.onCompletedCallback = undefined;
  }

  /**
   * Starts playing the count in audio.  Typically called by notesPlayer.
   * @param barDurationSeconds - used to determine when to schedule each sample. e.g. 4/4 with bar duration of 4 seconds would schedule "one" at 0, "two" at 1 second, "three" at 2 second, etc.
   * @param beatType - the 4 in 3/4. e.g. quarter note.  needed so we know which count in word combination to use.  e.g. 2/2 would be "one", "two", "set", "go"
   * @param beatsPerMeasure - the 3 in 3/4. needed so we know which count in word combination to use.
   * @param speed - 1 for 100%, .5 for 50%
   * @param onlyPlaySetGo - used when doing countin for loop.
   * @param dontPlaySound - when count is is disabled, we want to give the duration of "set, go" before starting, without hearing any sound saying "set, go"
   */
  async play({barDurationSeconds, beatType, beatsPerMeasure, speed, onlyPlaySetGo=false, dontPlaySound=false}: {countInCompletedCallback?: () => void; barDurationSeconds: number; beatsPerMeasure: number; beatType: number; speed: number, onlyPlaySetGo?: boolean, dontPlaySound?: boolean }): Promise<void> {
    this.stop();

    const isPlayingCountInSound: boolean = !dontPlaySound;

    const notes = createNotes({barDurationSeconds, beatsPerMeasure, beatType, onlyPlaySetGo});

    // our promise that will handle when to move on from countIn
    let onCompletedCallback: Promise<void>;


    if (isPlayingCountInSound){
      // will play count in audio
      // setup promise to track when audio completes
      onCompletedCallback = new Promise<void>((resolve) => {
        this.setOnCompletedCallback(resolve);
      });
      // schedule audio to start playing
      this.notesPlaybackScheduler.scheduleNotesPlayback({notes, synthesizer: this, speed, seekOffsetSeconds: 0, loopEndOffsetSeconds: -1});
    } else {
      // will not play count in audio
      // calculate how long we wait before starting
      let countInCompletesInNms = determineWhenCountInWillCompleteInMilliseconds(barDurationSeconds, speed);
      if(onlyPlaySetGo){
        countInCompletesInNms = notes.reduce((totalDurationMs, n) => totalDurationMs + n.duration * 1000, 0) / speed;
      }

      // setup promise to control when to let lesson page know when it can start playing music
      onCompletedCallback = new Promise<void>((resolve) => {
        // we are not playing audio, so we will use a timeout to resolve
        this.clearTimeoutForCountInCompleted = accurateTimingWorker.setTimeout(resolve, countInCompletesInNms);
      });
    }

    // return promise
    return onCompletedCallback;
  }

  /**
   * Stop all scheduled playbacks for all samples.
   * @param forceCallToCallback - stop everything, but still execute the callback that was set to execute once countin completeed.  Useful for cancelling countin due to the user seeking somewhere else.
   */
  stop(forceCallToCallback=false){
    if(this.clearTimeoutForCountInCompleted){
      this.clearTimeoutForCountInCompleted(forceCallToCallback);
      this.clearTimeoutForCountInCompleted = undefined;
    }
    this.notesPlaybackScheduler.cancelAllScheduledNotesPlayback(); //results in this.stopAllScheduledPlays being called.
  }

  /**
   * Finds the appropriate sample player to play for the given midi number.  Schedules the note for playback.
   * Called on by notesPlaybackScheduler.
   * @param startAtContextRelativeSeconds - point in time, relative to audioContext.currentTime, when the note should be played.
   * @param durationInSeconds - how long the note should be played for
   * @param velocity - how loud the note should be played
   * @param midi - midi number for the note
   */
  schedulePlayNote({startAtContextRelativeSeconds, durationInSeconds, velocity, midi}: {midi: number; velocity: number; startAtContextRelativeSeconds: number; durationInSeconds: number}){
    const samplePlayer = this.samplePlayers[midi];
    if(samplePlayer === undefined){ return logger.lessonAudio.error({message: `no sample player for midi note ${midi}`}); }
    const scheduledPlayPromise = samplePlayer.schedulePlay({volume: convertVelocityToVolume(velocity), startAtContextRelativeSeconds, durationInSeconds});

    if (midi === midiForSet){
      const actualDurationMidiSetMs = Math.floor((samplePlayer.audioBuffer.duration * 1000));
      scheduledPlayPromise.then(({timeStamp})=>{
        this.midiForSetStartedAtMs = (timeStamp - actualDurationMidiSetMs);
      })
    }
    if (midi === midiForGo){
      // how long the audio file will actually play
      const actualDurationMidiGoMs: number = Math.floor((samplePlayer.audioBuffer.duration * 1000));

      // audio is scheduled, handle after go completes playing
      scheduledPlayPromise
        .then(({timeStamp}) => {
          /**
           * We run this callback here to avoid a bug:
           *  Originally, the callback was scheduled to run some number of seconds after the start of the count-in.
           *  However, on lower-end devices (eg. Celeron N4020, 4GB RAM) in Chrome 103 (& likely before) & Electron,
           *  the count-in audio was played well after it was scheduled.
           *  From a user's perspective, this made it seem like the backing track was starting too early.
           *  Likely, this was due to the count-in being scheduled on a CPU thread which had high contention.
           *  As a quick fix, the backing track is scheduled to start some seconds after the last piece of audio ends.
           * If a more robust fix is needed in the future,
           *  we can evaluate putting count-in audio in a WebWorker,
           *  similar to how other audio is scheduled (eg. piano demo).
           */
          // time as ms when playback for midi go would have started
          const midiForGoStartedAtMs = (timeStamp - actualDurationMidiGoMs);

          /**
           * amount of time between when midi SET and midi GO would have started
           * There should be the same amount of time between the start of midi SET
           *  and the start of midi GO as between the start of midi GO and the
           *  lesson player starting.
           */
          const timeBetweenStartOfMidiSetAndMidiGoMs = (
            this.midiForSetStartedAtMs === undefined // should never be undefined at this point
              ? (durationInSeconds * 1000) // if something went wrong, fall back to passed play duration
              : midiForGoStartedAtMs - this.midiForSetStartedAtMs // when midiGo started - when midiSet started
          );

          // how long we should wait before continuing after midi GO ended
          const waitDurationAfterMidiGoCompleteMs: number = timeBetweenStartOfMidiSetAndMidiGoMs - actualDurationMidiGoMs;

          return new Promise<void>((resolve) => {
            // wait for the rest of the time that the playback of GO was supposed to take
            this.clearTimeoutForCountInCompleted = accurateTimingWorker.setTimeout(resolve, waitDurationAfterMidiGoCompleteMs);
          });
        })
        // notify countIn that go has completed, it is now time to continue
        .then(() => {
          this.runOnCompletedCallback();
        });
    }
  }

  /**
   * Tells each of the sample players to stop/cancel all scheduled playbacks.
   */
  stopAllScheduledPlays(){
    Object.values(this.samplePlayers).forEach(s => s.stopAllScheduledPlays());
  }
}

/**
 * We need to tell notesPlayer when it can begin playing notes again.
 * We assume that the count in will take 2 measures to complete, and factor in playback speed.
 * @param barDurationSeconds - how many seconds a bar is played for
 * @param speed - 1 for 100%, .5 for 50%, etc
 */
function determineWhenCountInWillCompleteInMilliseconds(barDurationSeconds: number, speed: number){
  const countInDurationInSeconds = barDurationSeconds * 2;
  const speedAdjustedCountInDurationInSeconds = countInDurationInSeconds / speed;
  return speedAdjustedCountInDurationInSeconds * 1000;
}

/**
 * Create a note for each word in the count in by determining the count in pattern based on the time signature.
 * Count in is expected to take place over 2 bars/measures.
 * e.g. 4/4 is "one", "two", "three", "four", "one", "two", "set", "go"
 * @param barDurationSeconds - how many seconds in length a bar/measure in the song lasts
 * @param beatsPerMeasure - the 3 in 3/4
 * @param beatType - the 4 in 4/4
 * @param duration - how many seconds the note should be played for
 * @param velocity - how loud the note should be played.
 * @param onlyPlaySetGo - when loop starts again, we only want to count "set go", rather than the full count in.
 */
function createNotes({barDurationSeconds=1, beatsPerMeasure=4, beatType=4, duration=1, velocity=100, onlyPlaySetGo=false}){
  const midiNumbersForTwoBars = getCountInMidiPatternForTimeSignature(beatsPerMeasure, beatType);
  const notesPerBar = Math.floor(midiNumbersForTwoBars.length / 2);
  const firstBarMidiNumbers = midiNumbersForTwoBars.slice(0, notesPerBar);
  const secondBarMidiNumbers = midiNumbersForTwoBars.slice(notesPerBar, midiNumbersForTwoBars.length);
  const offsetIncrement = barDurationSeconds / notesPerBar;
  let startOffset = 0;
  const barOneNotes = createNotesForMidiNumbers({midiNumbers: firstBarMidiNumbers, duration, velocity, offsetIncrement, barIndex: -2, startOffset });
  startOffset = offsetIncrement * firstBarMidiNumbers.length;
  const barTwoNotes = createNotesForMidiNumbers({midiNumbers: secondBarMidiNumbers, duration, velocity, offsetIncrement, barIndex: -1, startOffset });
  let notes = barOneNotes.concat(barTwoNotes);

  //when loop restarts, just play "set go" with a bit of blank space beforehand.
  if(onlyPlaySetGo){
    notes = notes.slice(Math.max(notes.length -2, 1));//only use the last two notes from the array, as they will always be set go.
    const blankSpaceDuration = offsetIncrement;
    const blankSpaceNote = createNotesForMidiNumbers({midiNumbers: [20], duration: blankSpaceDuration, velocity: 0, offsetIncrement, barIndex: -1, startOffset: 0})[0];
    notes[0].offset = blankSpaceDuration;
    notes[0].duration = offsetIncrement;
    notes[1].offset = offsetIncrement + notes[0].offset;
    notes[1].duration = offsetIncrement;
    notes = [blankSpaceNote, ...notes];
  }

  return notes;
}

/**
 * Iterates over the array of midiNumbers (from the count in pattern), creating a note for each midi number.
 * @param midiNumbers - array of midi numbers for the count in pattern. e.g. [-1, -2, -3, -4]
 * @param offsetIncrement - each note should start at a different time, where the difference in time between two notes is this number.
 * @param velocity - how loud to play the note
 * @param duration - how long to play the note
 * @param startOffset - the starting offset where notes should start from.  Useful for creating the second measure/bar of count in.
 * @param barIndex - bar index each note should have assigned.
 */
function createNotesForMidiNumbers({midiNumbers, offsetIncrement, velocity, duration, startOffset, barIndex}: {midiNumbers: number[]; offsetIncrement: number; velocity: number; duration: number; startOffset: number; barIndex: number}){
  const notes: Note[] = [];
  let offset = startOffset;
  let beatIndex = 0;
  //create the first bar
  for(let i = 0; i < midiNumbers.length; ++i){
    const path = new NotePath({barIndex, beatIndex: beatIndex++, trackIndex: 0, voiceIndex: 0, noteIndex: 0});
    const note = new Note({offset, duration, tieEnd: false, midi: midiNumbers[i], path, velocity, clef: 'bass' });
    notes.push(note);
    offset += offsetIncrement;
  }
  return notes;
}

/**
 * Lookup the count in pattern based on the time signature.
 * @param beatsPerMeasure - the 3 in 3/4
 * @param beatType - the 4 in 3/4
 */
function getCountInMidiPatternForTimeSignature(beatsPerMeasure: number, beatType: number): number[]{
  const key = `${beatsPerMeasure}_${beatType}`;
  return timeSignatureCountInMap[key] || timeSignatureCountInMap["4_4"];
}

/**
 * Create an instrument sample player for the provided url, and connect it to a destination.
 * @param assetFileName - url for mp3 file
 * @param destination - gain node
 */
function  buildCountInSamplePlayer(assetFileName: string, destination: AudioNode){
  return new InstrumentSampleAsyncPlayer({url: `${soundAssets.baseUrl}${soundAssets.countIn.relativePath}${assetFileName}`, destination,});
}

/**
 * Converts midi velocity to web audio volume format (e.g. 1, .5, etc)
 * @param velocity - midi velocity e.g. 127, 100, etc
 */
function convertVelocityToVolume(velocity?: number){
  return velocity !== undefined ? velocity / 127 : 1;
}

/**
 * Build an InstrumentSamplePlayer for each midi note used in the count in playback.
 * @param destination
 */
function buildCountInSamplePlayers(destination: AudioNode): IMidiNumberSamplePlayerHash{
  return {
    [midiForOne]: buildCountInSamplePlayer(soundAssets.countIn.sampleFileNames.one, destination),
    [midiForTwo]: buildCountInSamplePlayer(soundAssets.countIn.sampleFileNames.two, destination),
    [midiForThree]: buildCountInSamplePlayer(soundAssets.countIn.sampleFileNames.three, destination),
    [midiForFour]: buildCountInSamplePlayer(soundAssets.countIn.sampleFileNames.four, destination),
    [midiForSet]: buildCountInSamplePlayer(soundAssets.countIn.sampleFileNames.set, destination),
    [midiForGo]: buildCountInSamplePlayer(soundAssets.countIn.sampleFileNames.go, destination),
  };
}

const countIn = new CountIn();
export default countIn;